// Note di credito — regola del segno (decisa per iscritto, vale per tutta l'app):
// in `invoices` la nota di credito è salvata con importi POSITIVI, come vuole
// FatturaPA per il TD04. Il segno meno si applica solo quando si sommano
// documenti diversi (totali di lista, report): usare `signedAmount`.

export interface CreditAmounts {
  taxable_amount: number | null;
  tax_amount: number | null;
}

export interface LinkedCreditNote extends CreditAmounts {
  status: string | null;
}

/**
 * invoices.payment_status di una fattura stornata del tutto da note di credito emesse.
 * Lo imposta (e lo toglie) il trigger `on_credit_note_sync_invoice` nel database:
 * l'app lo legge soltanto. Non è un incasso e non è più "da incassare".
 */
export const CREDITED_PAYMENT_STATUS = 'credited';

/** Tipi di documento che si possono rettificare con una nota di credito. */
export const CREDITABLE_DOCUMENT_TYPES = ['invoice', 'invoice_with_delivery'] as const;

export const isCreditableDocumentType = (docType: string | null | undefined): boolean =>
  CREDITABLE_DOCUMENT_TYPES.includes((docType ?? 'invoice') as (typeof CREDITABLE_DOCUMENT_TYPES)[number]);

export const toCents = (value: number | null | undefined): number => Math.round((Number(value) || 0) * 100);

// Imponibile + IVA, bollo escluso: il bollo è un'imposta propria di ogni
// documento, non una parte del prezzo che si restituisce al cliente.
const creditBaseCents = (doc: CreditAmounts): number => toCents(doc.taxable_amount) + toCents(doc.tax_amount);

/** Quanto resta da stornare di una fattura, in centesimi (mai negativo). */
export function remainingCreditableCents(invoice: CreditAmounts, creditNotes: LinkedCreditNote[]): number {
  const used = creditNotes
    .filter((nc) => nc.status !== 'cancelled')
    .reduce((sum, nc) => sum + creditBaseCents(nc), 0);
  return Math.max(0, creditBaseCents(invoice) - used);
}

/** Importo con il segno giusto per sommare documenti diversi. */
export const signedAmount = (docType: string | null | undefined, amount: number | null | undefined): number =>
  (docType === 'credit_note' ? -1 : 1) * (Number(amount) || 0);
